import type { ButtonHTMLAttributes } from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '@/lib/cn';
import { Icon } from '../icon/Icon';
import type { OddsFlash, OddsState } from './OddsButton';
import { OddsFlashOverlay } from './OddsFlashOverlay';

const desktopOddsButton = cva(
  'relative flex h-11 min-w-0 flex-1 flex-col items-center justify-center gap-1 overflow-hidden ' +
    'rounded-sm p-1 text-center transition-colors',
  {
    variants: {
      state: {
        default: 'bg-bg-elevated hover:bg-odds-default',
        selected: 'bg-odds-selected',
        suspended: 'bg-odds-suspended cursor-not-allowed',
        locked: 'bg-bg-elevated cursor-not-allowed',
      },
    },
    defaultVariants: { state: 'default' },
  },
);

export interface DesktopOddsButtonProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'children' | 'value'
> {
  /** Short outcome label, e.g. `1`, `X`, `Over 2.5`. */
  outcome: string;
  /** Formatted price. Replaced by lock (locked) or em-dash (suspended). */
  odds: string;
  state?: OddsState;
  flash?: OddsFlash;
}

/** Desktop odds chip — outcome above, odds below. Use on DesktopEventRow. */
export function DesktopOddsButton({
  outcome,
  odds,
  state = 'default',
  flash = 'none',
  className,
  disabled,
  type = 'button',
  onClick,
  ...props
}: DesktopOddsButtonProps) {
  const unavailable = state === 'suspended' || state === 'locked';
  const selected = state === 'selected';
  const showFlash = flash !== 'none' && state !== 'suspended' && state !== 'locked';

  return (
    <button
      type={type}
      className={cn(desktopOddsButton({ state }), className)}
      aria-pressed={selected}
      aria-disabled={unavailable || undefined}
      {...props}
      disabled={disabled === true || unavailable}
      onClick={unavailable ? undefined : onClick}
    >
      {showFlash && <OddsFlashOverlay flash={flash} />}

      <span
        className={cn(
          'relative w-full truncate whitespace-nowrap text-[10px] leading-3',
          selected
            ? 'text-odds-selected-text'
            : unavailable
              ? 'text-text-disabled'
              : 'text-text-secondary',
        )}
      >
        {outcome}
      </span>
      <span className="relative flex shrink-0 items-center justify-center gap-0.5">
        {state === 'locked' ? (
          <Icon name="lock" size={14} label="Selection locked" className="text-icon-default" />
        ) : state === 'suspended' ? (
          <span
            className="text-[13px] leading-4 tabular-nums text-text-disabled"
            aria-label="Selection suspended"
          >
            —
          </span>
        ) : (
          <>
            {showFlash && (
              <span
                className={cn(
                  'text-[9px] leading-none font-bold',
                  flash === 'up' ? 'text-odds-up' : 'text-odds-down',
                )}
              >
                <span aria-hidden>{flash === 'up' ? '▲' : '▼'}</span>
                <span className="sr-only">{flash === 'up' ? 'price up' : 'price down'}</span>
              </span>
            )}
            <span
              className={cn(
                'text-[13px] leading-4 tabular-nums',
                selected ? 'text-odds-selected-text' : 'font-bold text-odds-default-text',
              )}
            >
              {odds}
            </span>
          </>
        )}
      </span>
    </button>
  );
}
